/**
 * Workflow Group Membership Store — persists the user's workflow permission
 * group memberships in TotalAccessOfflineDB (`app-shell` domain).
 *
 * This is the offline source of truth for which workflow tiles appear on
 * BeginActionModal. The data is fetched from `/api/v1/iam/my-workflow-memberships`
 * and synced via masterSync.ts.
 *
 * Storage shape: a single KV record per client containing the deduplicated
 * set of workflow keys the user is a group member of.
 */
import offlinePayloadCrypto from './offlineCrypto';
import type { OfflineDomainRecord } from './offlineDb';
import offlineDb from './offlineDb';

const DOMAIN = 'app-shell' as const;
const STORE = 'workflow-group-memberships' as const;
const ENTITY_ID = 'my-workflow-memberships';

export interface WorkflowGroupMembershipRecord {
  workflow_key: string;
  group_id: number;
  group_key: string;
  group_name: string;
  solution_key: string;
  role_in_group: string;
}

export interface StoredWorkflowGroupMemberships {
  workflow_keys: string[];
  memberships: WorkflowGroupMembershipRecord[];
  synced_at: string;
}

function getDb() {
  return offlineDb;
}

async function decryptRecordPayload<T>(record: OfflineDomainRecord | undefined): Promise<T | undefined> {
  if (!record) {
    return undefined;
  }
  return offlinePayloadCrypto.decryptValue<T>(record.payload);
}

async function getStoreRow(clientId: number): Promise<OfflineDomainRecord | undefined> {
  const db = getDb();
  if (!db) {
    return undefined;
  }
  return db.records
    .where('[domain+store+client_id+entity_id]')
    .equals([DOMAIN, STORE, clientId, ENTITY_ID])
    .first();
}

const workflowGroupMembershipStore = {
  /** Persist workflow group memberships for the current client. */
  async save(clientId: number, data: Omit<StoredWorkflowGroupMemberships, 'synced_at'>): Promise<void> {
    const db = getDb();
    if (!db || !clientId) {
      return;
    }

    const existing = await getStoreRow(clientId);
    const now = new Date().toISOString();
    const payload: StoredWorkflowGroupMemberships = {
      ...data,
      synced_at: now,
    };
    const encrypted = await offlinePayloadCrypto.encryptValue(payload);

    const row: OfflineDomainRecord = {
      id: existing?.id,
      domain: DOMAIN,
      store: STORE,
      client_id: clientId,
      entity_id: ENTITY_ID,
      sync_status: 'synced',
      created_at: existing?.created_at || now,
      updated_at: now,
      payload: encrypted,
      payload_size: encrypted.length,
    };

    await db.records.put(row);
  },

  /** Read cached workflow group memberships from the offline DB. */
  async load(clientId: number): Promise<StoredWorkflowGroupMemberships | null> {
    const row = await getStoreRow(clientId);
    const decrypted = await decryptRecordPayload<StoredWorkflowGroupMemberships>(row);
    if (decrypted === undefined || decrypted === null) {
      return null;
    }
    return decrypted as StoredWorkflowGroupMemberships;
  },

  /** Return the set of workflow keys the user is a member of (lowercased). */
  async loadWorkflowKeySet(clientId: number): Promise<Set<string>> {
    const stored = await this.load(clientId);
    if (!stored) {
      return new Set();
    }
    return new Set(stored.workflow_keys.map((k) => k.trim().toLowerCase()));
  },

  /** Clear cached memberships (e.g. on logout). */
  async clear(clientId: number): Promise<void> {
    const db = getDb();
    if (!db) {
      return;
    }
    const row = await getStoreRow(clientId);
    if (row?.id !== undefined && typeof row.id === 'number') {
      await db.records.delete(row.id);
    }
  },
};

export default workflowGroupMembershipStore;
